import {
  Alert,
  Box,
  Button,
  ProgressBar,
  Spinner,
} from '@razorpay/blade/components';
import api from 'api';
import { CreateCustomReportPayload, GetCustomReportPayload } from 'api/customReports';
import { Table } from 'components/ApprovalWorkflows/MyRequests/components/Table';
import { Pagination } from 'components/ApprovalWorkflows/MyRequests/components/Table/Pagination';
import { Column } from 'components/ApprovalWorkflows/MyRequests/components/Table/types';
import EmployeePayslipsAdmin from 'components/BulkExport/EmployeePayslipsAdmin';
import { CustomFieldFilter } from 'components/CustomFieldFilter';
import { CustomFieldFilterToggleButton } from 'components/CustomFieldFilter/FilterToggleButton';
import NoResultsView from 'components/Fnf/ReleaseNetpay/NoResultsVIew';
import {
  ResetBladePortalOverridingStyles,
  ResetLocalOverridingStyles,
} from 'components/RunPayroll/ComplianceActions/styles';
import permissions from 'constants/permissions';
import useFeature from 'hooks/useFeature';
import { useFilters } from 'hooks/useFilters';
import { useSearchParams } from 'hooks/useSearchParams';
import React, { useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { GetCustomReportSchemaContract } from '../../schemas/GetCustomReportSchema';
import ColumnSelector from './ColumnSelector';
import FilterTags from './FilterTags';
import ReportFilters from './ReportFilters';
import ReportInputs from './ReportInputs';
import { ReportPageWrapper } from './ReportPageWrapper';
import { ReportEmptyState } from './components/ReportEmptyState';
import { ReportErrorState } from './components/ReportErrorState';
import { ReportMetaBar } from './components/ReportMetaBar';
import {
  DEFAULT_PAGE_SIZE,
  DEFAULT_PAGE_SIZE_OPTIONS,
  HRIS_PEOPLE_IDS_FILTER_KEY,
  REPORT_QUEUE_ALERT_THRESHOLD_MS,
  customReportStatus,
} from './constants';
import { GET_CUSTOM_REPORT_CONFIG } from './queries';
import { placeholderColumns, renderColumnValue } from './reportHelper';
import { ReportAction, ReportFilter, ReportInput, ReportListItem, ReportPageLink } from './types';
import useCustomReportData from './useCustomReportData';
import useFiltersAndInputs from './useFiltersAndInputs';
import { useTimeThreshold } from './useTimeThreshold';
import { downloadCustomReport, filterHiddenColumns } from './utils';
import styles from './index.module.scss';

const padDatePart = (datePart: number) => String(datePart).padStart(2, '0');

const formatDateForLegacyBulkExport = (date: Date) =>
  `${padDatePart(date.getDate())}/${padDatePart(date.getMonth() + 1)}/${date.getFullYear()}`;

const getPayrollMonthListForPayslipExport = (payrollMonthInput?: ReportInput) => {
  return (payrollMonthInput?.options ?? []).reduce(
    (monthList, option) => {
      const [year, month] = option.key.split('-').map(Number);
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0);

      monthList[option.key.substring(0, 7)] = {
        text: option.value,
        startDate: formatDateForLegacyBulkExport(startDate),
        endDate: formatDateForLegacyBulkExport(endDate),
      };

      return monthList;
    },
    {} as Record<string, { text: string; startDate: string; endDate: string }>,
  );
};

export const CustomReport = () => {
  const [limit, onLimitChange] = useState(DEFAULT_PAGE_SIZE);
  const [sortedColumns, setSortedColumns] = useState<Record<string, 'asc' | 'desc'>>({});
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [page, onPageChange] = useState(1);
  const { urlParams } = useSearchParams();
  const type = urlParams.get('type');
  const [oldData, setOldData] = useState<GetCustomReportSchemaContract | null>(null);
  const [customFieldPeopleIds, setCustomFieldPeopleIds] = useState<number[] | null>(null);
  const [isCustomFieldFilterOpen, setIsCustomFieldFilterOpen] = useState(false);
  const [customFieldFilterCount, setCustomFieldFilterCount] = useState(0);
  const [visibleOptionalColumns, setVisibleOptionalColumns] = useState<string[]>([]);

  const { isHrisCustomFieldsEnabled } = useFeature('hrisCustomFields');
  const userPermissions = useSelector(loggedInUserSelectors.permissions);
  const canManageView = userPermissions.includes(permissions.MANAGE_CUSTOM_FIELDS);

  const { start, tick, hasBreachedThreshold, startTime } = useTimeThreshold({
    thresholdMs: REPORT_QUEUE_ALERT_THRESHOLD_MS,
  });

  const customReportConfigQuery = useQuery({
    queryFn: () => api.customReport.getCustomReportConfig(type),
    queryKey: [GET_CUSTOM_REPORT_CONFIG, type],
    onError: (err) => {
      console.log('Getting error while fetching the config', err);
    },
    onSuccess(data) {
      if (!data.isRealTime) {
        start();
      }
      const filters = (data?.filters ?? []) as ReportFilter[];
      applyFilters(filters.map((filter) => ({ filter: filter.key, value: filter.defaultValue })));
    },
    enabled: !!type,
  });

  const sortedKeys = Object.keys(sortedColumns);
  const sortByKey = sortedKeys.length ? sortedKeys[0] : null;
  const sortByOrder = sortByKey ? sortedColumns[sortByKey] : null;

  const isRealTime = customReportConfigQuery.data?.isRealTime ?? false;
  const inputs = (customReportConfigQuery.data?.inputs ?? []) as ReportInput[];
  const filters = (customReportConfigQuery.data?.filters ?? []) as ReportFilter[];
  const showCustomFieldFilter =
    isHrisCustomFieldsEnabled && Boolean(customReportConfigQuery.data?.supportsCustomFieldFilter);
  const configPageLinks = customReportConfigQuery.data?.pageLinks;
  const pageLinks: ReportPageLink[] = Array.isArray(configPageLinks)
    ? configPageLinks.filter(
        (link) => typeof link?.label === 'string' && typeof link?.href === 'string',
      )
    : [];
  const optionalColumns = customReportConfigQuery.data?.optionalColumns ?? [];
  const configuredActions = (customReportConfigQuery.data?.actions ?? []) as ReportAction[];
  const reportActions = configuredActions.length
    ? configuredActions
    : ([
        {
          key: 'downloadCsv',
          label: 'Download',
          type: 'customReportDownload',
        },
      ] as ReportAction[]);
  const topReportActions = reportActions.filter((action) => action.type === 'payslipBulkDownload');
  const footerReportActions = reportActions.filter((action) => action.type !== 'payslipBulkDownload');
  const payrollMonthInput = inputs.find((input) => input.key === 'payrollMonth');

  const { filtersLabelMap, filtersFieldsMap, inputsFieldsMap } = useFiltersAndInputs({
    inputs,
    filters,
  });

  const {
    applyFilter: applyInput,
    removeFilter: removeInput,
    currentFilters: currentInputs,
  } = useFilters({
    filtersMap: inputsFieldsMap,
  });
  const selectedPayrollMonth = String(getInputValue('payrollMonth') || '').substring(0, 7);

  const { applyFilter, removeFilter, applyFilters, currentFilters } = useFilters({
    filtersMap: filtersFieldsMap,
  });

  const createReportMutation = useMutation({
    mutationFn: () => {
      onPageChange(1);
      return api.customReport.createCustomReport({
        type,
        inputs: inputs.reduce(
          (acc, input) => ({ ...acc, [input.key]: currentInputs[input.key] || input.defaultValue }),
          {},
        ) as CreateCustomReportPayload['inputs'],
      });
    },
    onMutate: () => start(),
    onError: (err) => {
      console.log('Getting error while creating report', err);
      setErrorMessage(
        'Cannot generate report. Please try again. If issue persists, contact customer support.',
      );
    },
  });

  const customReportId = createReportMutation.data?.customReportId ?? null;

  // The reserved hris_people_ids entry must be present (even with an empty
  // value) whenever the custom-field filter is active: present-but-empty
  // means "filter active, zero matches" on the backend.
  const appliedFilters = {
    ...filters.reduce(
      (acc, filter) => ({
        ...acc,
        [filter.key]: currentFilters[filter.key],
      }),
      {},
    ),
    ...(customFieldPeopleIds !== null
      ? { [HRIS_PEOPLE_IDS_FILTER_KEY]: customFieldPeopleIds.join(',') }
      : {}),
  } as GetCustomReportPayload['filters'];

  const onCustomFieldPeopleIdsChange = (peopleIds: number[] | null) => {
    setCustomFieldPeopleIds(peopleIds);
    onPageChange(1);
  };

  const customReportQuery = useCustomReportData({
    customReportId,
    isRealTime,
    isConfigFetched: customReportConfigQuery.isFetched,
    type,
    filters: appliedFilters,
    onError() {
      setErrorMessage(
        'Cannot generate the report. Please try again. If issue persists, contact customer support.',
      );
    },
    onTick(data) {
      if (data?.status !== customReportStatus.STATUS_SUCCEEDED && !isRealTime) {
        tick();
      }
    },
    onSuccess(data) {
      setOldData(data);
    },
    sortByKey,
    sortByOrder,
    limit,
    page,
  });

  const isCreatingReport = createReportMutation.isLoading;
  const isReportStatusStarted =
    customReportQuery.data?.status === customReportStatus.STATUS_STARTED;

  // A failed config request (403 flag-off / no permission, or 400 bad type)
  // means we can never build the report. Render a clear terminal error with a
  // way back to the reports list instead of an indefinite loading state.
  if (customReportConfigQuery.isError) {
    return (
      <ReportPageWrapper reportName={customReportConfigQuery.data?.name} pageLinks={pageLinks}>
        <Box display="grid" placeItems="center" height="100%" paddingY="spacing.10">
          <NoResultsView
            title="Report unavailable"
            description="You don't have access to this report or it doesn't exist."
            actions={
              <Button variant="primary" href="/reports">
                Back to reports
              </Button>
            }
          />
        </Box>
      </ReportPageWrapper>
    );
  }

  if (
    isCreatingReport ||
    isReportStatusStarted ||
    (customReportId && oldData?.reportId !== customReportId)
  ) {
    return (
      <ReportPageWrapper reportName={customReportConfigQuery.data?.name} pageLinks={pageLinks}>
        <Box
          height="100%"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          gap="spacing.7"
          paddingY="spacing.10">
          <Spinner accessibilityLabel="Generating Report" size="large" />
          <Box display="flex" flexDirection="column" gap="spacing.7" alignItems="center">
            <Box width="200px">
              <ProgressBar
                label="Generating Report"
                value={customReportQuery.data?.progress ?? 0}
                size="medium"
                color="information"
              />
            </Box>
            {hasBreachedThreshold && (
              <Alert
                isDismissible={false}
                color="information"
                description="Your report is in the queue and will begin processing shortly. Due to high demand,
                this may take a few minutes"
              />
            )}
          </Box>
        </Box>
      </ReportPageWrapper>
    );
  }

  const data = oldData;

  const hiddenColumns = filterHiddenColumns(oldData?.response?.columns);
  const optionalColumnKeys = optionalColumns.map((c) => c.key);
  const userHiddenColumns = optionalColumnKeys.filter(
    (key) => !visibleOptionalColumns.includes(key),
  );
  const allHiddenColumns = [...hiddenColumns, ...userHiddenColumns];

  const internalColumns = oldData?.response?.columns?.filter(
    (column) => !allHiddenColumns.includes(column.key),
  );

  const columns: Column<ReportListItem>[] =
    internalColumns?.map((column, index) => {
      return {
        title: column.name,
        key: column.key,
        render: (item, index: any) => {
          return renderColumnValue({ column, item, index });
        },
        isSortable: true,
        styles: {
          width: '150px',
          minWidth: '150px',
          textAlign: index > 0 ? 'right' : 'left',
          position: column.config?.isSticky ? 'sticky' : 'relative',
          // Sticky columns need an opaque cell so the scrolling content does
          // not show through, so this cannot simply be dropped. The token
          // follows the active theme, unlike the fixed dark value it replaces
          // (which stayed near-black on the light theme).
          backgroundColor: 'var(--surface-background-gray-intense)',
          // Header colour intentionally omitted: the shared table already
          // falls back to the themed header background.
        },
      };
    }) || [];

  const tableData = oldData?.response?.rows?.map((row, index) => {
    const data = row.filter((item) => !allHiddenColumns.includes(item.key!));
    return { id: index, data: data, isChecked: false, allowAction: true } as ReportListItem;
  });

  const reportMetaBarActions = topReportActions.map((action) => {
    if (action.type !== 'payslipBulkDownload') {
      return null;
    }
    return (
      <div key={action.key} className={styles['salary-register-payslip-export']}>
        <EmployeePayslipsAdmin
          payrollMonthList={getPayrollMonthListForPayslipExport(payrollMonthInput)}
          selectedPayrollMonth={selectedPayrollMonth}
        />
      </div>
    );
  });

  const applyInputValue = (key: string, value: string | string[] | null) => {
    if (!value) {
      removeInput(key);
    } else {
      applyInput(key, value);
    }
  };

  const applyFilterValue = (key: string, value: string | string[] | null) => {
    if (!value) {
      removeFilter(key);
    } else {
      applyFilter(key, value);
    }
  };

  const onDownloadClick = () => {
    const activeDownloadFilters: Record<string, string> = {};
    filters.forEach((filter) => {
      const value = currentFilters[filter.key];
      if (value) {
        activeDownloadFilters[filter.key] = value;
      }
    });
    if (customFieldPeopleIds !== null) {
      // An empty value is intentional: present-but-empty means zero matches.
      activeDownloadFilters[HRIS_PEOPLE_IDS_FILTER_KEY] = customFieldPeopleIds.join(',');
    }

    downloadCustomReport({
      type,
      activeFilters: activeDownloadFilters,
      // Legacy report types always use the full GET export; only types that opt
      // in via supportsFilteredDownload POST their active filters.
      supportsFilteredDownload: Boolean(customReportConfigQuery.data?.supportsFilteredDownload),
    });
  };

  const isReportInteractionDisabled =
    customReportConfigQuery.isLoading || customReportQuery.isLoading;

  function getInputValue(key: string) {
    return currentInputs[key] || inputs.find((input) => input.key === key)?.defaultValue;
  }

  function handleReportAction(action: ReportAction) {
    if (action.type === 'customReportDownload') {
      onDownloadClick();
      return;
    }
  }

  return (
    <ReportPageWrapper
      isLoading={customReportConfigQuery.isLoading}
      isError={
        customReportConfigQuery.isError ||
        customReportQuery.data?.status === customReportStatus.STATUS_FAILED
      }
      errorView={
        <ReportErrorState centered onRetry={() => createReportMutation.mutate()} />
      }
      errorMessage={errorMessage}
      reportName={customReportConfigQuery.data?.name}
      pageLinks={pageLinks}>
      <ReportInputs
        isLoading={isReportInteractionDisabled}
        inputs={inputs as ReportInput[]}
        onChange={applyInputValue}
        currentInputs={currentInputs}
        centerLayout={
          <Box display="flex" gap="spacing.5" alignItems="center">
            {!isRealTime ? (
              <Button
                variant="secondary"
                isDisabled={customReportQuery.isLoading || isCreatingReport}
                onClick={() => createReportMutation.mutate()}>
                Generate New
              </Button>
            ) : null}
            {showCustomFieldFilter ? (
              <CustomFieldFilterToggleButton
                isOpen={isCustomFieldFilterOpen}
                isDisabled={isReportInteractionDisabled}
                appliedCount={customFieldFilterCount}
                accessibilityLabel="Custom field filters"
                onToggle={() => setIsCustomFieldFilterOpen((isOpen) => !isOpen)}
              />
            ) : null}
          </Box>
        }
        rightSideContent={
          optionalColumns.length > 0 ? (
            <ColumnSelector
              options={optionalColumns}
              selectedKeys={visibleOptionalColumns}
              onChange={setVisibleOptionalColumns}
            />
          ) : (
            <ReportFilters
              key={JSON.stringify(currentFilters)}
              isLoading={isReportInteractionDisabled}
              currentFilters={currentFilters}
              onApplyFilters={applyFilters}
              onChange={applyFilterValue}
              filters={filters as ReportFilter[]}
            />
          )
        }
      />
      {showCustomFieldFilter && isCustomFieldFilterOpen ? (
        <Box paddingX="spacing.7" paddingTop="spacing.5">
          <ResetBladePortalOverridingStyles />
          <ResetLocalOverridingStyles>
            <CustomFieldFilter
              disabled={isReportInteractionDisabled}
              canManageView={canManageView}
              onApply={(result) => onCustomFieldPeopleIdsChange(result.peopleIds)}
              onClear={() => onCustomFieldPeopleIdsChange(null)}
              onSelectedFiltersCountChange={setCustomFieldFilterCount}
            />
          </ResetLocalOverridingStyles>
        </Box>
      ) : null}
      {optionalColumns.length === 0 && (
        <FilterTags filtersMap={filtersFieldsMap} filtersLabelMap={filtersLabelMap} />
      )}
      <Box borderWidth="thin" borderColor="surface.border.gray.subtle" marginTop="spacing.7">
        <Box borderBottomWidth="thin" borderBottomColor="surface.border.gray.subtle">
          <Box>
            {data?.response?.rows?.length ? (
              <ReportMetaBar
                page={page}
                limit={limit}
                totalRows={data.response.totalRows}
                preparedAt={data.preparedAt}
                isRealTime={isRealTime}
                rightContent={reportMetaBarActions}
              />
            ) : null}
          </Box>
          {customReportQuery.isLoading && (
            <Box>
              <ProgressBar isIndeterminate />
            </Box>
          )}
        </Box>
        <Box>
          <Table
            id="table-custom-report"
            layout="auto"
            columns={columns.length ? columns : placeholderColumns}
            data={tableData || []}
            sortedColumns={sortedColumns}
            onSortChange={(sortKey, operation) =>
              setSortedColumns({
                [sortKey]: operation,
              })
            }
            isError={
              customReportQuery.isError ||
              customReportQuery.data?.status === customReportStatus.STATUS_FAILED
            }
            isLoading={customReportConfigQuery.isLoading || !oldData}
            isFetching={customReportQuery.isLoading}
            noResultsView={
              <ReportEmptyState
                hasGeneratedReport={Boolean(customReportQuery.data?.preparedAt)}
                isRealTime={isRealTime}
                onRefresh={() => customReportQuery.refetch()}
                isRefreshing={customReportQuery.isLoading}
              />
            }
            errorResultsView={
              <ReportErrorState
                onRetry={() => customReportQuery.refetch()}
                isRetrying={customReportQuery.isLoading}
              />
            }
          />
        </Box>

        <Pagination
          currentPage={page}
          pageSizeOptions={data?.response?.limitOptions ?? DEFAULT_PAGE_SIZE_OPTIONS}
          totalItems={data?.response?.totalRows ?? 1}
          itemsPerPage={limit ?? DEFAULT_PAGE_SIZE}
          onCurrentPageChange={onPageChange}
          onItemsPerPageChange={(value) => {
            onLimitChange(value);
            onPageChange(1);
          }}
          isDisabled={!oldData}
          leftContainer={
            <Box display="flex" gap="spacing.4">
              {footerReportActions.map((action) => (
                <Button
                  key={action.key}
                  isDisabled={
                    customReportQuery.isLoading ||
                    (action.type === 'customReportDownload' && !data?.reportId)
                  }
                  onClick={() => handleReportAction(action)}>
                  {action.label}
                </Button>
              ))}
            </Box>
          }
        />
      </Box>
    </ReportPageWrapper>
  );
};
